import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import {
  CurrencyDollar as Money, Bank, Star, Clock, IdentificationCard, Briefcase, Phone,
  Cake, TrendUp,
} from "@phosphor-icons/react";
import { Progress } from "../components/ui/progress";
import LinkCharacter from "../components/LinkCharacter";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n || 0);
const fmtMoney = (n) => `$${fmt(n)}`;
const fmtHours = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;

function StatCell({ label, value, sub, testId, tone = "default", icon: Icon }) {
  const toneCls = {
    default: "text-white",
    volt: "text-volt",
    danger: "text-tactical-danger",
    success: "text-tactical-success",
  }[tone];
  return (
    <div className="border border-tactical-border bg-tactical-surface p-5 hover:border-white/30 transition-colors duration-150" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{label}</div>
        {Icon && <Icon size={16} className="text-white/30" />}
      </div>
      <div className={`font-mono font-semibold text-2xl ${toneCls}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

export default function Character() {
  const { user } = useAuth();
  const [char, setChar] = useState(null);
  const [stats, setStats] = useState(null);

  const reload = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/character"), api.get("/character/stats")]);
      setChar(c.data); setStats(s.data);
    } catch (e) { toast.error("Charakter konnte nicht geladen werden"); }
  };

  useEffect(() => { reload(); }, []);

  if (!char) return <div className="p-12 text-white/40 font-mono text-sm">Lade Charakter...</div>;

  const xpPct = stats?.xp_to_next ? Math.round((stats.xp / stats.xp_to_next) * 100) : 0;
  const isLinked = char.source === "esx_sync";

  return (
    <div>
      <PageHeader
        eyebrow="01 // CHARACTER"
        title={`${char.first_name} ${char.last_name}`}
        description={`${char.job_grade_label} · ${char.job.toUpperCase()} · ${user?.username}`}
        right={
          <div className="font-mono text-right">
            <div className="text-[10px] text-white/40 uppercase tracking-widest">Level</div>
            <div className="text-4xl font-black text-volt">{char.level}</div>
          </div>
        }
      />

      <div className="p-4 md:p-8 lg:p-12 space-y-6 md:space-y-8">
        {!isLinked && <LinkCharacter onLinked={reload} />}

        {/* XP progress */}
        <div className="border border-tactical-border bg-tactical-surface p-6">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="font-mono text-xs text-white/40 uppercase tracking-widest">XP Progression</div>
              <div className="font-mono text-xl mt-1">{fmt(char.xp)} <span className="text-white/40 text-sm">/ {fmt(char.xp_to_next)}</span></div>
            </div>
            <div className="text-tactical-success font-mono text-xs">+{xpPct}% zum nächsten Level</div>
          </div>
          <Progress value={xpPct} className="h-1 bg-tactical-border" />
        </div>

        {/* Money grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          <StatCell testId="stat-cash" label="Cash" value={fmtMoney(char.cash)} sub="On hand" tone="success" icon={Money} />
          <StatCell testId="stat-bank" label="Bank" value={fmtMoney(char.bank)} sub="Fleeca Konto" tone="volt" icon={Bank} />
          <StatCell testId="stat-black" label="Schwarzgeld" value={fmtMoney(char.black_money)} sub="Undeclared" tone="danger" icon={Money} />
          <StatCell testId="stat-wealth" label="Gesamt" value={fmtMoney(stats?.total_wealth)} sub="Vermögen" icon={TrendUp} />
        </div>

        {/* Identity + Career */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="border border-tactical-border bg-tactical-surface p-6">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-4">Identität</div>
            <div className="space-y-3 font-mono text-sm">
              <Row icon={IdentificationCard} label="Full Name" value={`${char.first_name} ${char.last_name}`} />
              <Row icon={Cake} label="Geburtsdatum" value={char.dob} />
              <Row icon={IdentificationCard} label="Geschlecht" value={char.gender === "M" ? "Männlich" : "Weiblich"} />
              <Row icon={Phone} label="Handynummer" value={char.phone} />
            </div>
          </div>

          <div className="border border-tactical-border bg-tactical-surface p-6">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-4">Karriere</div>
            <div className="space-y-3 font-mono text-sm">
              <Row icon={Briefcase} label="Job" value={char.job.toUpperCase()} />
              <Row icon={Star} label="Rang" value={char.job_grade_label} />
              <Row icon={Clock} label="Playtime" value={fmtHours(char.playtime_minutes)} />
              <Row icon={TrendUp} label="XP" value={`${fmt(char.xp)} / ${fmt(char.xp_to_next)}`} />
            </div>
          </div>
        </div>

        {/* Quick counts */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          <StatCell testId="stat-vehicles" label="Fahrzeuge" value={stats?.vehicles_count ?? 0} sub="Garagenbestand" />
          <StatCell testId="stat-properties" label="Immobilien" value={stats?.properties_count ?? 0} sub="Häuser + Apts" />
          <StatCell testId="stat-record" label="Strafakte" value={stats?.record_count ?? 0} sub="Einträge" tone={stats?.record_count ? "danger" : "default"} />
          <StatCell testId="stat-warnings" label="Warnungen" value={stats?.warning_count ?? 0} sub="Admin-Warns" tone={stats?.warning_count ? "warn" : "default"} />
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-tactical-border/50 last:border-0">
      <div className="flex items-center gap-2 text-white/40">
        <Icon size={14} />
        <span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-white">{value}</div>
    </div>
  );
}
